import { createContext, ElementType, ReactNode, useContext } from 'react';

import { IconCheckmark } from '@components/icons/checkmark';
import { Menu } from '@components/menu';
import { getScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

export const PHONE_FORMAT_COUNTRY_CODE = 'country_code';
export const PHONE_FORMAT_NATIONAL_NUMBER = 'national_number';

export type FixedFormatOption = {
  value: string;
  label: string;
  example: string;
};

// Matches the example number the CSV import guide already shows.
const SAMPLE_COUNTRY_CODE = '+354';
const SAMPLE_NATIONAL_NUMBER = '600 8000';

const locale = {
  en: {
    countryCode: 'Country code',
    phoneNumber: 'Phone number',
    default: 'Default',
  },
};

const { t } = getScopedTranslate(locale);

export const getFixedFormatOptions = (type?: string): FixedFormatOption[] | undefined => {
  if (type === 'phone') {
    return [
      {
        value: PHONE_FORMAT_COUNTRY_CODE,
        label: t('countryCode'),
        example: SAMPLE_COUNTRY_CODE,
      },
      {
        value: PHONE_FORMAT_NATIONAL_NUMBER,
        label: t('phoneNumber'),
        example: SAMPLE_NATIONAL_NUMBER,
      },
    ];
  }
  return undefined;
};

export const getDefaultFormatExample = (type?: string): string | undefined => {
  return type === 'phone' ? `${SAMPLE_COUNTRY_CODE} ${SAMPLE_NATIONAL_NUMBER}` : undefined;
};

export const getFixedFormatLabel = (type?: string, format?: string): string => {
  const options = getFixedFormatOptions(type);
  const option = options?.find((item) => item.value === format);
  return option?.label ?? t('default');
};

const FixedFormatsContext = createContext(false);

export const useIsFixedFormatsEnabled = () => {
  return useContext(FixedFormatsContext);
};

export const FixedFormatsProvider = ({ children }: { children: ReactNode }) => {
  return <FixedFormatsContext.Provider value={true}>{children}</FixedFormatsContext.Provider>;
};

type FixedFormatDropdownProps = {
  format?: string;
  options: FixedFormatOption[];
  defaultExample?: string;
  onFormatChange: (format: string) => void;
  trigger: ElementType;
  triggerProps: Record<string, any>;
};

export const FixedFormatDropdown = ({
  format,
  options,
  defaultExample,
  onFormatChange,
  trigger,
  triggerProps,
}: FixedFormatDropdownProps) => {
  const normalizedFormat = format && format !== 'undefined' ? format : '';

  return (
    <Menu
      trigger={trigger}
      triggerProps={triggerProps}
      placement="bottom-start"
      width={pxToRem(256)}
    >
      {({ close }) => {
        const isDefaultSelected = !normalizedFormat;
        return (
          <>
            <Menu.Item
              label={t('default')}
              helpText={defaultExample}
              isActive={isDefaultSelected}
              extraIconFullOpacity
              extraIcon={
                isDefaultSelected ? (
                  <IconCheckmark size={24} color="buttonIconPositive" />
                ) : undefined
              }
              onSelect={() => {
                onFormatChange('');
                close();
              }}
            />
            {options.map((option) => {
              const isSelected = normalizedFormat === option.value;
              return (
                <Menu.Item
                  key={option.value}
                  label={option.label}
                  helpText={option.example}
                  isActive={isSelected}
                  extraIconFullOpacity
                  extraIcon={
                    isSelected ? <IconCheckmark size={24} color="buttonIconPositive" /> : undefined
                  }
                  onSelect={() => {
                    onFormatChange(option.value);
                    close();
                  }}
                />
              );
            })}
          </>
        );
      }}
    </Menu>
  );
};
